import { EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { SearchQuery } from '../models/search-query.model';
import { ServiceRepairFinderFilter, SERVICE_REPAIR_AIRFRAME_OPTIONS, SERVICE_REPAIR_AIRFRAME_MANUFACTURER_OPTIONS, SERVICE_REPAIR_AVIONICS_OPTIONS, SERVICE_REPAIR_AVIONICS_MANUFACTURER_OPTIONS, SERVICE_REPAIR_POWERPLANT_OPTIONS, SERVICE_REPAIR_POWERPLANT_MANUFACTURER_OPTIONS, SERVICE_REPAIR_PROPELLER_OPTIONS, SERVICE_REPAIR_PROPELLER_MANUFACTURER_OPTIONS, SERVICE_REPAIR_INTERIOR_OPTIONS, SERVICE_REPAIR_EXTERIOR_OPTIONS, SERVICE_REPAIR_ROTOR_OPTIONS } from '../../shared/models/service-repair-finder-filter.model';
import { ServiceRepairFinderFiltersService } from '../../shared/services/service-repair-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
/**
 * Filter UI for the Service/Repair Finder, mirroring {@link FboFinderFilterListComponent}. Service/repair
 * has many facets (the Maintenance categories, each with a Manufacturers subsection), so its groups are
 * collapsible accordion sections with a selected-count badge, and a selected-filter chip row. Reads and
 * writes the shared {@link ServiceRepairFinderFiltersService} state and emits `filtersChange`.
 */
var ServiceRepairFinderFilterListComponent = /** @class */ (function () {
    function ServiceRepairFinderFilterListComponent(_serviceRepairFinderFiltersService, _analyticsService) {
        this._serviceRepairFinderFiltersService = _serviceRepairFinderFiltersService;
        this._analyticsService = _analyticsService;
        this.filtersChange = new EventEmitter();
        /**
         * The collapsible facet sections, in display order: the group key on ServiceRepairFinderFilter, the header
         * label, and the checkbox options. Service groups use AND logic and Manufacturer subsections use OR logic
         * (see filterServiceRepairs).
         */
        this.sections = [
            { key: 'airframe', title: 'Airframe', options: SERVICE_REPAIR_AIRFRAME_OPTIONS,
                manufacturers: { key: 'airframeManufacturers', title: 'Airframe Manufacturers', options: SERVICE_REPAIR_AIRFRAME_MANUFACTURER_OPTIONS } },
            { key: 'avionics', title: 'Avionics', options: SERVICE_REPAIR_AVIONICS_OPTIONS,
                manufacturers: { key: 'avionicsManufacturers', title: 'Avionics Manufacturers', options: SERVICE_REPAIR_AVIONICS_MANUFACTURER_OPTIONS } },
            { key: 'powerplant', title: 'Powerplant', options: SERVICE_REPAIR_POWERPLANT_OPTIONS,
                manufacturers: { key: 'powerplantManufacturers', title: 'Powerplant Manufacturers', options: SERVICE_REPAIR_POWERPLANT_MANUFACTURER_OPTIONS } },
            { key: 'propeller', title: 'Propeller', options: SERVICE_REPAIR_PROPELLER_OPTIONS,
                manufacturers: { key: 'propellerManufacturers', title: 'Propeller Manufacturers', options: SERVICE_REPAIR_PROPELLER_MANUFACTURER_OPTIONS } },
            { key: 'interior', title: 'Interior', options: SERVICE_REPAIR_INTERIOR_OPTIONS },
            { key: 'exterior', title: 'Exterior', options: SERVICE_REPAIR_EXTERIOR_OPTIONS },
            { key: 'rotor', title: 'Rotor', options: SERVICE_REPAIR_ROTOR_OPTIONS }
        ];
        /**
         * Per-section expand/collapse state. There are far more service/repair facets than fit on one screen, so
         * sections start collapsed; any section that already has selected filters is auto-expanded on first load so
         * restored/active filters are visible. Each header shows a count badge.
         */
        this.expanded = {
            airframe: false,
            airframeManufacturers: false,
            avionics: false,
            avionicsManufacturers: false,
            powerplant: false,
            powerplantManufacturers: false,
            propeller: false,
            propellerManufacturers: false,
            interior: false,
            exterior: false,
            rotor: false
        };
        this._expansionInitialized = false;
        /** Max number of selected-filter chips to render before collapsing the rest into a "+N more" marker. */
        this.maxChips = 5;
        /** The chips actually shown (first {@link maxChips} selected filters, in section + option order). */
        this.visibleChips = [];
        /** How many selected filters are hidden beyond {@link maxChips} (0 = none hidden). */
        this.overflowCount = 0;
        this.serviceRepairFinderFilter = new ServiceRepairFinderFilter();
    }
    Object.defineProperty(ServiceRepairFinderFilterListComponent.prototype, "nameTooShort", {
        /**
         * True when a business name has been entered but is too short to count toward a search. Mirrors the
         * `serviceRepairKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while
         * the entered name would be ignored.
         */
        get: function () {
            var trimmed = this.serviceRepairFinderFilter.name ? this.serviceRepairFinderFilter.name.trim() : '';
            return trimmed.length > 0 && trimmed.length < 3;
        },
        enumerable: true,
        configurable: true
    });
    ServiceRepairFinderFilterListComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._subscription = this._serviceRepairFinderFiltersService.serviceRepairFinderFilter.subscribe(function (f) {
            _this.serviceRepairFinderFilter = f;
            _this._recomputeChips();
            // Auto-expand sections with active filters once (on first load); don't fight the user's manual toggles
            // on subsequent emissions (each checkbox edit re-emits the same filter).
            if (!_this._expansionInitialized) {
                _this._expansionInitialized = true;
                _this.sections.forEach(function (s) {
                    var mfr = s.manufacturers;
                    var mfrHas = mfr ? !!(f && f[mfr.key] && f[mfr.key].length) : false;
                    var svcHas = !!(f && f[s.key] && f[s.key].length);
                    _this.expanded[s.key] = svcHas || mfrHas; // parent opens if anything inside (service or mfr) is selected
                    if (mfr) {
                        _this.expanded[mfr.key] = mfrHas; // manufacturer subsection opens only if it has selections
                    }
                });
            }
        });
        this._analyticsService.pageView('airport_landingpage_servicerepair', 'Service/Repair Finder Landing Page filters');
    };
    ServiceRepairFinderFilterListComponent.prototype.ngOnDestroy = function () {
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    };
    ServiceRepairFinderFilterListComponent.prototype.isFacetSelected = function (group, value) {
        return this.serviceRepairFinderFilter[group].indexOf(value) !== -1;
    };
    ServiceRepairFinderFilterListComponent.prototype.onToggleFacet = function (group, value, checked) {
        var selected = this.serviceRepairFinderFilter[group];
        var i = selected.indexOf(value);
        if (checked && i === -1) {
            selected.push(value);
        }
        else if (!checked && i !== -1) {
            selected.splice(i, 1);
        }
        this._emit();
    };
    ServiceRepairFinderFilterListComponent.prototype.onSelectAll = function (group, options) {
        this.serviceRepairFinderFilter[group] = options.map(function (o) { return o.value; });
        this._emit();
    };
    ServiceRepairFinderFilterListComponent.prototype.onClearAll = function (group) {
        this.serviceRepairFinderFilter[group] = [];
        this._emit();
    };
    ServiceRepairFinderFilterListComponent.prototype.onNameChange = function () {
        this._emit();
    };
    ServiceRepairFinderFilterListComponent.prototype.toggleSection = function (group) {
        this.expanded[group] = !this.expanded[group];
    };
    /** Number of selected filters in a single group (shown as the count badge on that group's header). */
    ServiceRepairFinderFilterListComponent.prototype.selectedCount = function (group) {
        var selected = this.serviceRepairFinderFilter[group];
        return selected ? selected.length : 0;
    };
    /**
     * Total selected filters for a parent type section — its own services plus its nested Manufacturers
     * subsection — so the parent badge still reflects manufacturer selections while the subsection is collapsed.
     */
    ServiceRepairFinderFilterListComponent.prototype.sectionBadgeCount = function (section) {
        var count = this.selectedCount(section.key);
        if (section.manufacturers) {
            count += this.selectedCount(section.manufacturers.key);
        }
        return count;
    };
    ServiceRepairFinderFilterListComponent.prototype.trackByChip = function (index, chip) {
        return chip.value;
    };
    /**
     * Rebuilds the selected-filter chip list from the current filter state (called on every filter change).
     * Chips are collected in section + option order; only the first {@link maxChips} are shown, the rest are
     * summarized by {@link overflowCount}.
     */
    ServiceRepairFinderFilterListComponent.prototype._recomputeChips = function () {
        var _this = this;
        var chips = [];
        var collect = function (key, options) {
            var selected = _this.serviceRepairFinderFilter[key] || [];
            options.forEach(function (option) {
                if (selected.indexOf(option.value) !== -1) {
                    chips.push({ group: key, value: option.value, label: option.label });
                }
            });
        };
        this.sections.forEach(function (section) {
            collect(section.key, section.options);
            if (section.manufacturers) {
                collect(section.manufacturers.key, section.manufacturers.options);
            }
        });
        this.visibleChips = chips.slice(0, this.maxChips);
        this.overflowCount = Math.max(0, chips.length - this.maxChips);
    };
    ServiceRepairFinderFilterListComponent.prototype._emit = function () {
        // Keep the query's filter in sync as the user edits, so a business name entered here counts toward search
        // validation (see SearchQuery.validate) even before the query is serialized/submitted.
        if (this.query) {
            this.query.serviceRepairFinderFilter = this.serviceRepairFinderFilter;
        }
        this._serviceRepairFinderFiltersService.updateServiceRepairFinderFilters(this.serviceRepairFinderFilter);
        this.filtersChange.emit();
    };
    return ServiceRepairFinderFilterListComponent;
}());
export { ServiceRepairFinderFilterListComponent };
